import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import buildModifiedMaterial from './customMesh/modifiedMaterial.js'
import buildDepthMaterial from './customMesh/depthMaterial.js'

import gsap from 'gsap'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js'

export default class World{
  constructor(){
    // Variables
    this.canvas = document.getElementById('webgl')
    this.scene = new THREE.Scene()
    this.camera
    this.renderer

    // Variables
    this.cubeScale = 10
    this.cubeOffset = 1.01
    this.positions = []
    this.sizes
    this.spotLight
    this.uniforms = {
      uTime: { value: 0 },
      uMouse: {value: new THREE.Vector3(0, 0, 0)},
      uColor1: { value: new THREE.Color( 0xe2bb60 ) },
      uColor2: { value: new THREE.Color( 0x749275 ) }
    }
    this.cursorPositon = new THREE.Vector3()
    this.colorTargets = [
      this.uniforms.uColor1.value,
      this.uniforms.uColor2.value
    ]
    this.freeColors = false
    this.inputColor1
    this.inputColor2

    // Setup
    this.setCamera()
    this.setOrbitControl()
    this.setRenderer()
    this.setLights()

    this.calculatePositions()
    this.createCube()
    this.postProcess()

    // Wall // Raycast
    this.wall
    this.pointer

    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.cursorEvent()
    this.createWall()

    this.uiEvents()
    
    setInterval(() => {
      if(this.freeColors){
        this.refreshColorTargets()
      }
    }, 4000)
    this.refreshColorTargets()

    // Loop
    const tick = () => {
      // Render
      this.effectComposer.render(this.scene, this.camera)

      // Control
      this.orbitControl.update()

      // Loop
      window.requestAnimationFrame(tick)

      // Calculate hitbox
      this.updateWallCursor()

      // Update from cursorPosition
      this.updateFromCursor()

      // Update Time
      this.uniforms.uTime.value = performance.now()

      // Refresh Colors
      if(this.freeColors){
        this.refreshColors()
      }
      this.dancingFire()
    }
    tick()

    // Resize
    window.addEventListener('resize', () => {
      this.resize()
    })
    this.resize()
  }

  setCamera(){
    this.camera = new THREE.PerspectiveCamera(75, 0, 0.01, -1)
    this.camera.position.set(10, 15, 15)

    this.scene.add(this.camera)
  }

  setOrbitControl(){
    this.orbitControl = new OrbitControls(this.camera, this.canvas)
    this.orbitControl.enableDamping = true
    this.orbitControl.screenSpacePanning = false
    this.orbitControl.enablePan = false

    this.orbitControl.maxDistance = 50
  }

  setRenderer(){
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas
    })

    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFShadowMap
    this.renderer.physicallyCorrectLights = true

    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.outputEncoding = THREE.sRGBEncoding
  }

  setLights(){
    // Spot
    this.spotLight = new THREE.DirectionalLight( 0xffffff, 0.5)

    this.spotLight.position.set(20, 20, 20)
    this.spotLight.castShadow = true
    this.spotLight.angle = Math.PI * 0.3

    this.spotLight.shadow.camera.near = -5
    this.spotLight.shadow.camera.far = 50

    this.spotLight.shadow.camera.top = 8
    this.spotLight.shadow.camera.bottom = -8
    this.spotLight.shadow.camera.right = 8
    this.spotLight.shadow.camera.left = -8

    this.spotLight.shadow.mapSize.width = 1024;
    this.spotLight.shadow.mapSize.height = 1024;
    
    const ambiantLight = new THREE.AmbientLight(0xffffff, 0.5)
    this.scene.add(ambiantLight)

    this.scene.add(this.spotLight)
  }

  calculatePositions(){
    for(let x = 0 ; x < this.cubeScale; x++){
      for(let y = 0 ; y < this.cubeScale; y++){
          for(let z = 0 ; z < this.cubeScale; z++){
          this.positions.push(new THREE.Vector3(x, y, z))
        }
      }
    }
  }

  createCube(){
    const length = this.positions.length

    // Populate cubes
    const buffer = BufferGeometryUtils.mergeBufferGeometries([new THREE.BoxGeometry(1, 1, 1)])
    const bufferList = []
    const arrayPos = new Float32Array(length * 216)
    const arrayCenter = new Float32Array(length * 216)

    for(let i = 0; i < length; i++){
      const pos = this.positions[i]
      const newGeometry = buffer.clone()

      // Position
      newGeometry.translate(
        pos.x * this.cubeOffset, 
        pos.y * this.cubeOffset,
        pos.z * this.cubeOffset,
      )

      const localId = i * 72
      for (let y = 0; y < 72; y++){
        const id = localId + (y * 3)

        arrayPos[id] = (pos.x * this.cubeOffset) - this.cubeScale / 2
        arrayPos[id + 1] = (pos.y * this.cubeOffset) - this.cubeScale / 2
        arrayPos[id + 2] = (pos.z * this.cubeOffset) - this.cubeScale / 2

        arrayCenter[id] = pos.x * this.cubeOffset
        arrayCenter[id + 1] = pos.y * this.cubeOffset
        arrayCenter[id + 2] = pos.z * this.cubeOffset
      }

      bufferList.push(newGeometry)
    }

    // Mesh
    const geometry = BufferGeometryUtils.mergeBufferGeometries(bufferList)
    geometry.setAttribute('aPos', new THREE.BufferAttribute(arrayPos, 3))
    geometry.setAttribute('aCenter', new THREE.BufferAttribute(arrayCenter, 3))

    const mesh = new THREE.Mesh(
      geometry,
      buildModifiedMaterial(this.uniforms)
    )

    mesh.customDepthMaterial = buildDepthMaterial(this.uniforms)
    mesh.castShadow = true
    mesh.receiveShadow = true

    mesh.position.set(
      - this.cubeScale / 2,
      - this.cubeScale / 2,
      - this.cubeScale / 2
    )

    this.scene.add(mesh)
  }

  createWall(){
    this.wall = new THREE.Mesh(
      new THREE.PlaneGeometry(200, 200),
      new THREE.MeshBasicMaterial()
    )
    this.wall.visible = false

    this.pointer = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 15, 15),
      new THREE.MeshStandardMaterial({
        color: this.uniforms.uColor2.value
      })
    )

    this.scene.add(this.wall)
    this.scene.add(this.pointer)
  }

  updateWallCursor(){
    // Refresh wall Position
    let cameraAngle = new THREE.Vector3()
    this.camera.getWorldDirection(cameraAngle)

    this.wall.position.set(
      -cameraAngle.x * 3,
      -cameraAngle.y * 3,
      -cameraAngle.z * 3,
    )

    // Refresh wall Rotation
    this.wall.lookAt(this.camera.position)

    // Raycast
    this.raycaster.setFromCamera(this.mouse, this.camera)

    const intersect = this.raycaster.intersectObject(this.wall)
    if(intersect.length > 0){
      gsap.to(this.cursorPositon, {
        x: intersect[0].point.x,
        y: intersect[0].point.y,
        z: intersect[0].point.z,
        duration: 1
      })
    }
  }

  updateFromCursor(){
    this.spotLight.position.copy(this.cursorPositon)
    this.pointer.position.copy(this.cursorPositon)

    this.uniforms.uMouse.value = this.cursorPositon
  }

  cursorEvent(){
    // Mouse Event
    this.canvas.addEventListener('mousemove', event => {
      this.setMousePosition(event.clientX, event.clientY)
    })   
    this.canvas.addEventListener('touchmove', event => {
      this.setMousePosition(event.changedTouches[0].clientX, event.changedTouches[0].clientY)
    })
  }

  setMousePosition(x, y){
    this.mouse.x = ( x / this.sizes.width ) * 2 - 1;
    this.mouse.y = - ( y / this.sizes.height ) * 2 + 1;
  }

  uiEvents(){
    this.inputColor1 = document.getElementById('inputColor1')
    this.inputColor2 = document.getElementById('inputColor2')
    const checkFree = document.getElementById('checkFree')

    this.inputColor1.value = `#${this.uniforms.uColor1.value.getHexString()}`
    this.inputColor2.value = `#${this.uniforms.uColor2.value.getHexString()}`

    inputColor1.addEventListener('input', () => {
      this.uniforms.uColor1.value = new THREE.Color(this.inputColor1.value)
    })
    inputColor2.addEventListener('input', () => {
      this.uniforms.uColor2.value = new THREE.Color(this.inputColor2.value)
      this.pointer.material.color = new THREE.Color(this.inputColor2.value)
    })
    checkFree.addEventListener('input', () => {
      this.freeColors = checkFree.checked
      if(this.freeColors){        
        this.refreshColorTargets()
      }
    })
  }

  refreshColors(){
    const color1 = this.uniforms.uColor1.value
    const color2 = this.uniforms.uColor2.value

    this.uniforms.uColor1.value = new THREE.Color(
      color1.r + ((this.colorTargets[0].r - color1.r) * 0.0042),
      color1.g + ((this.colorTargets[0].g - color1.g) * 0.0042),
      color1.b + ((this.colorTargets[0].b - color1.b) * 0.0042),
    )
    this.uniforms.uColor2.value = new THREE.Color(
      color2.r + ((this.colorTargets[1].r - color2.r) * 0.0042),
      color2.g + ((this.colorTargets[1].g - color2.g) * 0.0042),
      color2.b + ((this.colorTargets[1].b - color2.b) * 0.0042),
    )
    
    this.inputColor1.value = `#${this.uniforms.uColor1.value.getHexString()}`
    this.inputColor2.value = `#${this.uniforms.uColor2.value.getHexString()}`
  }

  refreshColorTargets(){
    this.colorTargets = [
      new THREE.Color( 
        this.colorDice(this.colorTargets[0].r),
        this.colorDice(this.colorTargets[0].g),
        this.colorDice(this.colorTargets[0].b) 
      ),
      new THREE.Color( 
        this.colorDice(this.colorTargets[1].r),
        this.colorDice(this.colorTargets[1].g),
        this.colorDice(this.colorTargets[1].b)
      )
    ]
  }

  colorDice(value){
    let dice = (Math.random() - 0.5)
    if(dice < 0.2 && dice > 0)
    {
      dice += 0.2
    }
    else if(dice > -0.2 && dice < 0){
      dice -= 0.2
    }

    let newColor = value + dice 

    if(newColor > 1 || newColor < 0){
      newColor = value - dice
    }

    return newColor
  }

  postProcess(){
    this.effectComposer = new EffectComposer(this.renderer)
  
    const renderPass = new RenderPass(this.scene, this.camera)
    this.effectComposer.addPass(renderPass)
  
    this.unrealBloomPass = new UnrealBloomPass()
    this.unrealBloomPass.strength = 0.5
    this.unrealBloomPass.radius = 1
    this.unrealBloomPass.threshold = 0
    this.effectComposer.addPass(this.unrealBloomPass)

    const smaaPass = new SMAAPass()
    this.effectComposer.addPass(smaaPass)
  }

  dancingFire(){
    const dice = (Math.random() - 0.5) * 0.05
    this.unrealBloomPass.strength += this.unrealBloomPass.strength > 0.5 ? dice - 0.0004 : dice + 0.0004;
  }

  resize(){
    this.sizes = {
      height: window.innerHeight,
      width: window.innerWidth
    }

    // Update renderer
    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    
    // Update camera
    this.camera.aspect = this.sizes.width / this.sizes.height
    this.camera.updateProjectionMatrix()
    
    // Resize Composer
    this.effectComposer.setSize(this.sizes.width, this.sizes.height)
    this.effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  }
}